import { type PickMember, durationUnit, picksSummary } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import type { ExpenseResponseDto } from '@splitfiesta/shared';
import { Button } from '@splitfiesta/ui';

interface PicksCardProps {
  expense: ExpenseResponseDto;
  members: readonly PickMember[];
  viewerId: string;
  /** Shown to a participant of the bill who can pick now. */
  onPickMine?: () => void;
}

const CARD_CLASS =
  'flex flex-col gap-3 rounded-2xl border border-primary-500 bg-primary-50 p-4 dark:border-primary-700 dark:bg-primary-950/40';
const TITLE_CLASS =
  'min-w-[45%] flex-1 text-base font-bold text-gray-900 dark:text-gray-50';
const ASIDE_CLASS = 'ml-auto text-sm text-gray-600 dark:text-gray-300';

/** The expense's card once the table was asked to pick. Read-only on web: no Remind, no saying who had the rest. */
export function PicksCard({
  expense,
  members,
  viewerId,
  onPickMine,
}: PicksCardProps) {
  const { t } = useTranslation('expenses');
  const summary = picksSummary(expense, members);

  if (summary.done) {
    const duration =
      summary.doneInMs != null ? durationUnit(summary.doneInMs) : null;
    return (
      <section data-testid="picks-card" className={CARD_CLASS}>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h3 className={TITLE_CLASS}>
            {t('picks.done.title', {
              picked: summary.itemsPicked,
              count: summary.itemsTotal,
            })}
          </h3>
          {duration ? (
            <span className={ASIDE_CLASS}>
              {t('picks.done.duration', {
                duration: t(`picks.duration.${duration.unit}`, {
                  count: duration.count,
                }),
              })}
            </span>
          ) : null}
        </div>
        <p className="text-sm text-gray-700 dark:text-gray-200">
          {t('picks.done.body')}
        </p>
      </section>
    );
  }

  const progress =
    summary.itemsTotal > 0 ? summary.itemsPicked / summary.itemsTotal : 0;
  const people = expense.lineItemParticipants?.length ?? expense.splits.length;
  const canPick =
    !!onPickMine && (expense.lineItemParticipants ?? []).includes(viewerId);
  const pickLabel = t(
    expense.pickedBy?.some(p => p.userId === viewerId)
      ? 'lineItems.changeMyItems'
      : 'picks.pickMine'
  );

  return (
    <section data-testid="picks-card" className={CARD_CLASS}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        {summary.peopleDone > 0 ? (
          <>
            <h3 className={TITLE_CLASS}>
              {t('picks.havePicked', {
                picked: summary.peopleDone,
                count: summary.peopleTotal,
              })}
            </h3>
            <span className={ASIDE_CLASS}>
              {t('picks.itemsOf', {
                picked: summary.itemsPicked,
                count: summary.itemsTotal,
              })}
            </span>
          </>
        ) : (
          <>
            <h3 className={TITLE_CLASS}>
              {t('picks.itemsPicked', {
                picked: summary.itemsPicked,
                count: summary.itemsTotal,
              })}
            </h3>
            {summary.peopleKnow > 0 ? (
              <span className={ASIDE_CLASS}>
                {t('picks.peopleKnow', { count: summary.peopleKnow })}
              </span>
            ) : null}
          </>
        )}
      </div>
      <div
        data-testid="picks-progress"
        aria-hidden="true"
        className="h-2 overflow-hidden rounded-full bg-primary-100 dark:bg-primary-900/60"
      >
        <div
          className="h-full rounded-full bg-primary-500"
          style={{ width: `${Math.round(progress * 100)}%` }}
        />
      </div>
      <p className="text-sm text-gray-700 dark:text-gray-200">
        {t(`picks.untilPick.${summary.method}`, { count: people })}
      </p>
      {canPick ? (
        <Button
          type="button"
          variant="primary"
          onClick={onPickMine}
          className="min-h-11 self-start"
        >
          {pickLabel}
        </Button>
      ) : null}
    </section>
  );
}

/** The group's members as the pick helpers read them; a placeholder has no account to pick with. */
export function pickMembersOf(
  members:
    | ReadonlyArray<{
        userId: string | { _id: string };
        isPlaceholder?: boolean;
      }>
    | undefined
): PickMember[] {
  return (members ?? []).map(m => ({
    userId: typeof m.userId === 'string' ? m.userId : m.userId._id,
    ...(m.isPlaceholder && { isPlaceholder: true }),
  }));
}

export default PicksCard;
